import { Box, BoxProps, Paper, Stack, TextField } from "@mui/material";
import axios, { AxiosRequestConfig } from "axios";
import {
  ControlPosition,
  HtmlMarker,
  MapMouseEvent,
  StyleControlOptions,
  TargetedEvent,
} from "azure-maps-control";
import "azure-maps-control/dist/atlas.min.css";
import { useCallback, useMemo, useState } from "react";
import {
  AzureMap,
  AzureMapHtmlMarker,
  ControlOptions,
  IAzureMapOptions,
  useAzureMaps,
} from "react-azure-maps";

import { GeoHelper } from "@/common/helpers/geo";
import { IdHelper } from "@/common/helpers/id";
import { TypeHelper } from "@/common/helpers/type";
import { useMemoAsync } from "@/common/hooks/memo/useMemoAsync";
import { useLatestRef } from "@/common/hooks/ref/useLatestRef";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { azureMapsService } from "@/common/services/azureMaps";
import { GeoMarker, GeoMarkerOptions, GeoPointCoordinates } from "@/common/ts/geo";

import AppIconButton from "../Button/AppIconButton";
import AppIcon from "../Icons/AppIcon";

function canDragMarker(props: { markers: Nil<MarkersProps>; marker: Nil<GeoMarker> }): boolean {
  return (
    !!props.markers?.canDrag &&
    (props.markers.options?.draggable ?? props.marker?.options?.draggable ?? true)
  );
}

export interface MarkersProps {
  markers: Nil<GeoMarker[]>;
  max?: number;
  options?: Partial<GeoMarkerOptions>;
  /** Initially zoom map at first marker. */
  isZoomAtFirst?: boolean;
  /** Initially center map at first marker. */
  isCenterAtFirst?: boolean;
  canAdd?: boolean;
  canRemove?: boolean;
  canDrag?: boolean;
  onAdded?: (newValue: GeoMarker) => void;
  onRemoved?: (newValue: GeoMarker) => void;
  onChanged?: (newValue: GeoMarker) => void;
  onCoordinatesChanged?: (newValue: GeoMarker) => void;
}

export interface GeolocationMapProps {
  zoom?: number;
  centerCoordinates?: GeoPointCoordinates;
  /** Display and edit markers on the map. */
  markers?: MarkersProps;
  searchProps?: {
    onCoordinatesFound: (newValue: GeoPointCoordinates) => void;
  };
  sx?: BoxProps["sx"];
}

export default function GeolocationMap({
  zoom,
  centerCoordinates,
  markers,
  searchProps,
  sx,
}: GeolocationMapProps) {
  const { enqueueSnackbar } = useAppSnackbar();
  const mapContext = useAzureMaps();

  const markersRef = useLatestRef(markers);

  const [search, setSearch] = useState<string | undefined>(undefined);

  const authSasTokenMemo = useMemoAsync(azureMapsService.getAuthSasToken, []);
  const authSasToken = authSasTokenMemo.data;

  const mapOptions = useMemo<Nil<IAzureMapOptions>>(() => {
    if (!authSasToken) {
      return null;
    }

    const firstMarker = markers?.markers?.at(0);
    const zoomAtFirstMarker =
      markers?.isZoomAtFirst && firstMarker ? GeoHelper.mapZoomPresets.street : undefined;
    const centerAtFirstMarker =
      markers?.isCenterAtFirst && firstMarker?.coordinates
        ? GeoHelper.getCoordinatesAsArray(firstMarker.coordinates)
        : undefined;
    const options: IAzureMapOptions = {
      authOptions: azureMapsService.getAuthOptions({
        authSasToken: authSasToken || undefined,
      }),
      zoom: zoomAtFirstMarker ?? zoom ?? 0,
      center:
        centerAtFirstMarker ||
        (centerCoordinates ? [centerCoordinates.longitude, centerCoordinates.latitude] : undefined),
    };
    return options;
  }, [authSasToken]);

  const handleMapClick = useCallback((e: MapMouseEvent) => {
    if (!e.position) {
      return;
    }
    if (!markersRef.current) {
      return;
    }
    if (
      !TypeHelper.isNil(markersRef.current.max) &&
      (markersRef.current.markers?.length ?? 0) >= markersRef.current.max
    ) {
      return;
    }

    const newMarker: GeoMarker = {
      id: IdHelper.newUuid4(),
      coordinates: {
        longitude: e.position[0],
        latitude: e.position[1],
      },
      options: {
        ...markersRef.current.options,
        draggable: canDragMarker({ markers: markersRef.current, marker: undefined }),
      },
    };
    markersRef.current.onAdded && markersRef.current.onAdded(newMarker);
  }, []);

  const handleSearch = async () => {
    if (!search || !search.trim()) {
      return;
    }

    // search place and get its geolocation
    // https://learn.microsoft.com/en-us/rest/api/maps/search/get-search-fuzzy?view=rest-maps-1.0&tabs=HTTP
    const format = "json";
    const searchURL = new URL(`https://atlas.microsoft.com/search/fuzzy/${format}`);
    const queryStringParams = {
      "api-version": "1.0",
      query: search,
      limit: "1",
    };
    // searchURL.search = new URLSearchParams(queryStringParams).toString();
    const requestConfig: AxiosRequestConfig<any> = {
      url: searchURL.toString(),
      params: queryStringParams,
    };
    azureMapsService.setAuthForRestApiRequest({ requestConfig, sasToken: authSasToken });

    try {
      const response = await axios.request(requestConfig);
      const result = response.data?.results?.at(0);
      if (!result || !result.position) {
        enqueueSnackbar("Geolocation not found.", { variant: "warning" });
        return;
      }

      const coordinates: GeoPointCoordinates = {
        longitude: result.position.lon,
        latitude: result.position.lat,
      };
      setSearch(undefined);
      searchProps?.onCoordinatesFound && searchProps?.onCoordinatesFound(coordinates);

      mapContext.mapRef?.setCamera({
        center: [coordinates.longitude, coordinates.latitude],
        zoom: 14,
        duration: 1000,
      });

      enqueueSnackbar(
        `Geolocation found: ${result.poi?.name || result.address?.freeformAddress || "-"}`,
        { variant: "success" },
      );
    } catch (error) {
      enqueueSnackbar("Search failed.", { variant: "error" });
      console.error("search failed:", error);
    }
  };

  if (!mapOptions) {
    return null;
  }

  return (
    <Box>
      <Box
        component='div'
        sx={{
          position: "relative",
          width: "100%",
          height: { xxs: 200, xs: 300, md: 400, lg: 500 },
          ...sx,
        }}
      >
        <AzureMap
          options={mapOptions}
          controls={[
            {
              controlName: "StyleControl",
              controlOptions: { mapStyles: "all" } as StyleControlOptions,
              options: { position: ControlPosition.TopRight } as ControlOptions,
            },
            {
              controlName: "ZoomControl",
              options: { position: ControlPosition.BottomRight } as ControlOptions,
            },
            {
              controlName: "FullscreenControl",
              options: { position: ControlPosition.BottomRight } as ControlOptions,
            },
          ]}
          // NB: these events are registered once so you need to pass memoized callbacks and use refs to access the latest state
          events={{
            click: handleMapClick,
          }}
        >
          <Box>
            {markersRef.current?.markers
              ?.filter((x) => GeoHelper.isValidPointCoordinates(x.coordinates))
              .map((marker) => {
                const canDrag = canDragMarker({ markers: markersRef.current, marker: undefined });

                return (
                  <AzureMapHtmlMarker
                    key={marker.id}
                    // isPopupVisible
                    options={{
                      ...marker.options,
                      position: marker.coordinates
                        ? [marker.coordinates.longitude, marker.coordinates.latitude]
                        : undefined,
                      draggable: canDrag,
                      // popup: (
                      //   <AzureMapPopup
                      //     isVisible
                      //     options={{ position: [0, 0] }}
                      //     popupContent={<div style={{ padding: "20px" }}>Hello World</div>}
                      //   />
                      // ),
                    }}
                    events={[
                      {
                        eventName: "dragstart",
                        callback: (e: TargetedEvent) => {
                          if (!canDrag) {
                            return;
                          }
                          const htmlMarker = e.target as HtmlMarker;
                          const options = htmlMarker.getOptions();
                          console.log("marker.dragstart", e, {
                            htmlMarker,
                            options,
                          });
                        },
                      },
                      {
                        eventName: "dragend",
                        callback: (e: TargetedEvent) => {
                          if (!canDrag) {
                            return;
                          }
                          const htmlMarker = e.target as HtmlMarker;
                          const options = htmlMarker.getOptions();
                          console.log("marker.dragend", e, {
                            htmlMarker,
                            options,
                          });

                          if (!options.position) {
                            return;
                          }
                          const [longitude, latitude] = options.position;
                          markersRef.current?.onCoordinatesChanged &&
                            markersRef.current?.onCoordinatesChanged({
                              ...marker,
                              coordinates: { longitude, latitude },
                              options,
                            });
                        },
                      },
                    ]}
                  />
                );
              })}
          </Box>
        </AzureMap>

        {/* Custom controls */}
        {mapContext.isMapReady && searchProps && (
          <Paper sx={{ position: "absolute", top: 10, left: 10, p: 1 }}>
            {/* Search */}
            {searchProps && (
              <Stack direction='row' spacing={1}>
                <TextField
                  label='Search location'
                  type='text'
                  variant='outlined'
                  size='small'
                  value={search || ""}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                />
                <AppIconButton variant='default' size='small' onClick={handleSearch}>
                  <AppIcon of='search' />
                </AppIconButton>
              </Stack>
            )}
          </Paper>
        )}
      </Box>
    </Box>
  );
}
